/**
 * @fileoverview Directors row for the job overview Details card.
 *
 * Lists current and former directors from the job CRM and opens the
 * existing contact card sheet when a name is clicked. Shown only on
 * corporate appointments — personal insolvency jobs do not mount this.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { api } from "@cvx/_generated/api";
import type { Id } from "@cvx/_generated/dataModel";
import { Button } from "@/ui/primitives/button";
import { ContactDetailPanel } from "@/components/jobs/phases/contacts/ContactDetailPanel";
import { ROLE_META } from "@/lib/contact-roles";
import { CopyableValue } from "./CopyableValue";
import { selectDirectorContacts } from "./entity-directors-detail.helpers";

interface EntityDirectorsDetailProps {
  jobId: Id<"jobs">;
}

export function EntityDirectorsDetail({ jobId }: EntityDirectorsDetailProps) {
  const [viewingContactId, setViewingContactId] =
    useState<Id<"jobContacts"> | null>(null);

  const { data: contacts } = useQuery(
    convexQuery(api.contacts.queries.listJobContacts, { jobId }),
  );

  const directors = useMemo(
    () => selectDirectorContacts(contacts ?? []),
    [contacts],
  );

  return (
    <div className="flex flex-col gap-1">
      <span className="text-xs text-muted-foreground">
        {ROLE_META.director.pluralLabel}
      </span>
      {directors.length === 0 ? (
        <span className="text-sm">—</span>
      ) : (
        // List directors inline (wrapping) like the Team card rather than a
        // tall vertical stack — a long director list otherwise stretched this
        // grid cell and threw out the alignment of the rest of the card. Each
        // name keeps its own hover-to-copy control and click-to-open card.
        <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
          {directors.map((director) => (
            <CopyableValue
              key={director.id}
              value={director.displayName}
              label={director.displayName}
            >
              <div className="flex items-center whitespace-nowrap text-sm">
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  className="h-auto p-0"
                  onClick={() => setViewingContactId(director.id)}
                  title={`Open ${director.displayName}'s contact card`}
                >
                  {director.displayName}
                </Button>
                {director.isFormer && (
                  <span className="ml-1.5 text-xs text-muted-foreground">
                    {ROLE_META.former_director.label}
                  </span>
                )}
              </div>
            </CopyableValue>
          ))}
        </div>
      )}
      <ContactDetailPanel
        jobId={jobId}
        contactId={viewingContactId}
        open={viewingContactId !== null}
        onClose={() => setViewingContactId(null)}
        jobContacts={contacts ?? []}
      />
    </div>
  );
}
